import { RouteEntry } from '../types/manifest';
import wpCategoriesRaw from './wpCategories.json';

export interface WpDbProduct {
  id: string;
  path: string;
  slug: string;
  title: string;
  sku: string;
  price: string;
  categories: Array<{ slug: string; name: string }>;
  shortIntro: string;
  shortBullets: string[];
  contentHtml: string;
  specGroups: ProductSpecGroup[];
  gallery: Array<{ localPath: string; originalUrl: string; alt: string }>;
  faqs?: ProductFaqItem[];
}

export interface WpDbPost {
  id: string;
  path: string;
  slug: string;
  title: string;
  date: string;
  categories: Array<{ slug: string; name: string }>;
  excerpt: string;
  contentHtml: string;
  featuredImage: { localPath: string; originalUrl: string; alt: string } | null;
  faqs?: ProductFaqItem[];
}

const wpCategories = wpCategoriesRaw as unknown as {
  productCategories: Array<{ slug: string; name: string; description: string; parentSlug: string; count: number }>;
  postCategories: Array<{ slug: string; name: string; description: string; parentSlug: string; count: number }>;
};

const prodCache: Record<string, WpDbProduct> = {};
const postCache: Record<string, WpDbPost> = {};

if (typeof window !== 'undefined') {
  const initDb = (window as unknown as {
    __INITIAL_ROUTE_DB__?: { kind: 'product' | 'post'; path: string; slug: string; data: WpDbProduct | WpDbPost };
  }).__INITIAL_ROUTE_DB__;
  if (initDb && initDb.data) {
    if (initDb.kind === 'product') {
      prodCache[initDb.path] = initDb.data as WpDbProduct;
    } else if (initDb.kind === 'post') {
      postCache[initDb.path] = initDb.data as WpDbPost;
      if (initDb.slug) {
        postCache[`/${initDb.slug}/`] = initDb.data as WpDbPost;
      }
    }
  }
}

export async function preloadRouteDb(route: RouteEntry): Promise<boolean> {
  const slug = route.path.replace(/^\/+|\/+$/g, '').split('/').pop();
  if (!slug) return false;
  if (route.type === 'product') {
    if (prodCache[route.path]) return true;
    try {
      const res = await fetch(`/data/db/prod__${slug}.json`);
      if (!res.ok) return false;
      const data = (await res.json()) as WpDbProduct;
      prodCache[route.path] = data;
      return true;
    } catch {
      return false;
    }
  }
  if (route.type === 'post') {
    if (postCache[route.path]) return true;
    try {
      const res = await fetch(`/data/db/post__${slug}.json`);
      if (!res.ok) return false;
      const data = (await res.json()) as WpDbPost;
      postCache[route.path] = data;
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

export interface ProductSpecRow {
  label: string;
  value: string;
}

export interface ProductSpecGroup {
  groupTitle: string;
  rows: ProductSpecRow[];
}

export interface ProductPromoBox {
  badgeTitle: string;
  subtitle?: string;
  items: string[];
  theme: 'barcode' | 'gift' | 'software';
}

export interface ProductArticleBlock {
  type: 'heading' | 'subheading' | 'paragraph' | 'list' | 'image' | 'video' | 'comparison' | 'callout';
  id?: string;
  text?: string;
  items?: string[];
  imageSrc?: string;
  imageAlt?: string;
  imageCaption?: string;
  videoSrc?: string;
  videoPoster?: string;
  youtubeId?: string;
  calloutTitle?: string;
}

export interface ProductFaqItem {
  q: string;
  a: string;
}

export interface RichProductData {
  path: string;
  categoryLabel: string;
  sku: string;
  brand: string;
  origin: string;
  warranty: string;
  statusText: string;
  priceNote: string;
  shortIntro?: string;
  shortBullets: string[];
  promoBox: ProductPromoBox;
  articleBlocks: ProductArticleBlock[];
  rawContentHtml?: string;
  specGroups: ProductSpecGroup[];
  faqs: ProductFaqItem[];
  gallery?: Array<{ localPath: string; originalUrl: string; alt: string }>;
}

export const SHOWCASE_5_PRODUCTS: Array<{
  path: string;
  shortLabel: string;
  categoryTag: string;
}> = [
  {
    path: '/product/phan-mem-quay-video-dong-hang-ecampmcs/',
    shortLabel: '1. Phần mềm ECAMPMCS',
    categoryTag: 'Phần Mềm Đóng Hàng'
  },
  {
    path: '/product/may-kiem-kho-opticon-px-36/',
    shortLabel: '2. Máy Kiểm Kho Opticon PX-36',
    categoryTag: 'Thiết Bị Mã Vạch / PDA'
  },
  {
    path: '/product/pin-sac-du-phong-belkin-10000mah-mau-hong/',
    shortLabel: '3. Pin Dự Phòng Belkin 10,000mAh',
    categoryTag: 'Sạc Dự Phòng Quốc Tế'
  },
  {
    path: '/product/loa-bluetooth-kiem-sac-khong-day-sp002/',
    shortLabel: '4. Loa Bluetooth Sạc Không Dây SP002',
    categoryTag: 'Loa & Sạc Không Dây LED'
  },
  {
    path: '/product/set-qua-tang-cong-nghe-5in1-gs504/',
    shortLabel: '5. Set Quà Tặng 5in1 GS504',
    categoryTag: 'Set Quà Tặng Doanh Nghiệp'
  }
];

export const RICH_PRODUCTS_BY_PATH: Record<string, RichProductData> = {
  // ==========================================================================
  // 1. PHẦN MỀM QUAY VIDEO ĐÓNG HÀNG ECAMPMCS (DANH MỤC: PHẦN MỀM)
  // ==========================================================================
  '/product/phan-mem-quay-video-dong-hang-ecampmcs/': {
    path: '/product/phan-mem-quay-video-dong-hang-ecampmcs/',
    categoryLabel: 'Phần Mềm Quay Video Đóng Hàng',
    sku: 'ECAMPMCS-PRO',
    brand: 'PMCS TECH (Bản Quyền Sở Hữu Trí Tuệ)',
    origin: 'Việt Nam (Phát triển bởi đội ngũ kỹ sư PMCS TECH)',
    warranty: 'Hỗ trợ kỹ thuật 1-1 & Cập nhật tính năng miễn phí suốt thời hạn License',
    statusText: 'Sẵn sàng kích hoạt Key • Cài đặt từ xa qua UltraViewer',
    priceNote: 'Giá phần mềm tính theo đầu máy tính (12 tháng / 1 PC): Gói 1 bàn 3.000.000đ/năm (~8.000đ/ngày) • ECAM Extend (2+ bàn) 5.000.000đ/năm (~14.000đ/ngày) • ECAM Ultimate 8.500.000đ/năm • Combo phần cứng từ 4.500.000đ/bàn',
    shortIntro:
      'Giải pháp phần mềm ghi hình đối soát đơn hàng thương mại điện tử chuyên nghiệp đã triển khai cho 390+ doanh nghiệp. Giá bản quyền tính theo đầu máy tính (PC): Gói 1 bàn đóng hàng 3.000.000đ/năm/máy tính (~8.000đ/ngày | $120/year/PC), gói ECAM Extend (từ 2 bàn trở lên) 5.000.000đ/năm/máy tính (~14.000đ/ngày | $200/year/PC), gói ECAM Ultimate 8.500.000đ/năm/máy tính ($340/year/PC), Combo phần cứng Camera đóng hàng từ 4.500.000đ/bàn đóng gói — mỗi PC không giới hạn số bàn đóng gói, số lượng video, thời lượng video và thời hạn lưu trữ.',
    shortBullets: [
      'Ghi lại toàn bộ quá trình đóng gói hàng hóa bằng video rõ nét kèm Mã vận đơn & Timestamp thời gian thực, tra cứu video trong 3–5 giây.',
      'Chống gian lận, làm bằng chứng đối soát minh bạch khi có khiếu nại từ khách hàng hoặc sàn TMĐT (Shopee, TikTok Shop, Lazada), đã triển khai tại 390+ doanh nghiệp.',
      '3 gói bản quyền phần mềm: Gói 1 bàn 3.000.000đ/năm/PC (~8.000đ/ngày); ECAM Extend (từ 2 bàn trở lên) 5.000.000đ/năm/PC (~14.000đ/ngày); ECAM Ultimate 8.500.000đ/năm/PC.',
      'Combo phần cứng Camera đóng hàng chuyên dụng (Camera toàn cảnh + Camera soi vận đơn + Giá treo + Màn hình tín hiệu) từ 4.500.000đ/bàn đóng gói.',
      'Nguyên tắc cấp phép theo đầu máy tính: mỗi PC KHÔNG GIỚI HẠN số bàn đóng gói / không giới hạn số lượng video / không giới hạn thời lượng video / không giới hạn thời hạn lưu trữ.',
      'Chia sẻ Video qua CDN miễn phí 1-Click và hỗ trợ 3 chế độ làm việc: Đóng hàng (Pack), Kiểm hàng (Check), Trả hàng / Hoàn hàng (Return).'
    ],
    promoBox: {
      badgeTitle: 'Ưu Đãi Triển Khai Giải Pháp ECAMPMCS',
      subtitle: 'Hỗ trợ cài đặt & hướng dẫn vận hành 1-1 cho kho hàng',
      theme: 'software',
      items: [
        'Tặng 07 ngày dùng thử miễn phí đầy đủ tính năng trước khi quyết định mua bản quyền.',
        'Miễn phí băng thông chia sẻ video qua hệ thống CDN tốc độ cao của PMCS TECH.',
        'Hỗ trợ kỹ thuật từ xa qua UltraViewer / AnyDesk xuyên suốt quá trình vận hành kho.',
        'Xuất hóa đơn GTGT (VAT) hợp lệ cho doanh nghiệp.'
      ]
    },
    articleBlocks: [
      {
        type: 'heading',
        id: 'gioi-thieu-ecampmcs',
        text: 'Phần Mềm Quay Video Đóng Hàng ECAMPMCS – Giải Pháp Quản Lý Đơn Hàng Minh Bạch & Hiệu Quả'
      },
      {
        type: 'paragraph',
        text: 'Bạn đang đau đầu vì khách hàng khiếu nại thiếu hàng, sai mẫu hoặc hàng hóa bị tráo đổi trong quá trình vận chuyển? Bạn cần một hệ thống lưu trữ bằng chứng đóng gói tự động, rõ ràng và tra cứu tức thì? Phần mềm quay video đóng hàng ECAMPMCS do Công ty TNHH Thương Mại & Dịch Vụ PMCS phát triển chính là giải pháp toàn diện dành cho nhà bán hàng thương mại điện tử và trung tâm hoàn thiện đơn hàng (Fulfillment Center).'
      },
      {
        type: 'image',
        imageSrc: '/wp-content/uploads/2025/05/Ecam-video.png',
        imageAlt: 'Giao diện phần mềm quay video đóng hàng ECAMPMCS kết nối đa camera',
        imageCaption: 'Giao diện trực quan của phần mềm ECAMPMCS quản lý đồng thời nhiều bàn đóng gói trên 1 màn hình trung tâm.'
      },
      {
        type: 'heading',
        id: 'tinh-nang-noi-bat',
        text: 'Tính Năng Nổi Bật Của Phần Mềm ECAMPMCS'
      },
      {
        type: 'list',
        items: [
          'Ghi hình tự động khi quét mã vận đơn: Kết nối trực tiếp với máy quét mã vạch (Opticon, Zebra, Honeywell...) hoặc camera soi mã, tự động bắt đầu và kết thúc video theo từng đơn hàng.',
          'Đóng dấu thông tin lên Video (OSD Overlay): Tự động chèn Mã vận đơn, Tên bàn đóng gói, Chế độ làm việc, Logo thương hiệu và Thời gian thực (Timestamp) trực tiếp vào khung hình video.',
          'Đa luồng hiệu năng cao: Một máy tính cấu hình phổ thông (Intel Core i3-14100, RAM 8GB) có thể xử lý mượt mà từ 10 đến 16 luồng Camera HD cùng lúc, phục vụ 5 đến 8 nhân viên đóng gói.',
          'Chia sẻ video qua CDN miễn phí: Không cần tải file nặng lên Google Drive hay YouTube. Chỉ cần bấm nút "Chia sẻ", phần mềm tự động sinh đường link xem video trực tuyến tốc độ cao để gửi ngay cho Shopee, TikTok Shop, Lazada hoặc khách mua hàng.',
          'Lưu trữ cục bộ an toàn 100%: Toàn bộ dữ liệu video được lưu trữ trực tiếp trên ổ cứng máy tính của doanh nghiệp, bảo mật tuyệt đối bí mật kinh doanh và không tốn phí thuê bao lưu trữ đám mây hàng tháng.'
        ]
      },
      {
        type: 'image',
        imageSrc: '/wp-content/uploads/2025/04/dong-goi-don-hang-shopee-5.jpg',
        imageAlt: 'Bố trí bàn đóng gói đơn hàng Shopee TikTok kết hợp camera và máy quét mã vạch',
        imageCaption: 'Mô hình lắp đặt thực tế: Camera toàn cảnh + Camera soi mã vận đơn kết nối về máy tính chạy ECAMPMCS.'
      },
      {
        type: 'heading',
        id: 'loi-ich-doanh-nghiep',
        text: 'Lợi Ích Thiết Thực Khi Triển Khai ECAMPMCS Tại Kho Hàng'
      },
      {
        type: 'list',
        items: [
          'Tỷ lệ thắng khiếu nại đạt gần như tuyệt đối (99%+) nhờ video có đầy đủ góc quay toàn cảnh và góc soi chi tiết mã vận đơn.',
          'Nâng cao ý thức trách nhiệm của nhân viên kho, giảm thiểu sai sót nhầm size, thiếu phụ kiện trong ca làm việc.',
          'Tra cứu lại lịch sử video của bất kỳ đơn hàng nào chỉ trong 1 giây bằng cách nhập hoặc quét lại mã vận đơn.',
          'Xây dựng hình ảnh thương hiệu chuyên nghiệp, uy tín trong mắt khách hàng khi đính kèm mã QR tra cứu video đóng gói trên phiếu gửi hàng.'
        ]
      },
      {
        type: 'comparison',
        id: 'bang-so-sanh-ecampmcs',
        text: 'Bảng So Sánh Phần Mềm ECAMPMCS Với Các Giải Pháp Khác Trên Thị Trường'
      }
    ],
    specGroups: [
      {
        groupTitle: 'Thông Tin Bản Quyền Phần Mềm',
        rows: [
          { label: 'Tên phần mềm', value: 'ECAMPMCS (Packing Video Management Software — Triển khai 390+ doanh nghiệp)' },
          { label: 'Đơn vị sở hữu bản quyền', value: 'CÔNG TY TNHH PMCS TECH (MST: 0315773444)' },
          { label: 'Bảng giá bản quyền', value: 'Gói 1 bàn: 3.000.000đ/năm/PC (~8.000đ/ngày) • ECAM Extend (2+ bàn): 5.000.000đ/năm/PC (~14.000đ/ngày) • ECAM Ultimate: 8.500.000đ/năm/PC' },
          { label: 'Combo phần cứng Camera', value: 'Từ 4.500.000đ/bàn đóng gói (Camera toàn cảnh + Camera soi vận đơn + Giá treo + Màn hình tín hiệu)' },
          { label: 'Số bàn đóng gói / 1 PC', value: 'Không giới hạn số bàn đóng gói trên mỗi máy tính (tùy cấu hình PC)' },
          { label: 'Số lượng video & đơn hàng', value: 'Không giới hạn số lượng video / số đơn hàng' },
          { label: 'Thời lượng & Thời hạn lưu', value: 'Không giới hạn thời lượng video & Không giới hạn thời hạn lưu trữ' }
        ]
      },
      {
        groupTitle: 'Khả Năng Kết Nối & Xử Lý Đa Luồng',
        rows: [
          { label: 'Số lượng Camera / 1 PC', value: 'Không giới hạn trên phần mềm (PC Core i3-14100 xử lý mượt 10 – 16 Camera / 5 – 8 bàn)' },
          { label: 'Chuẩn Camera hỗ trợ', value: 'Camera IP (RTSP/ONVIF) & Webcam USB' },
          { label: 'Thiết bị đọc mã hỗ trợ', value: 'Máy quét mã vạch 1D/2D (USB/Không dây) hoặc Camera đọc mã trực tiếp' },
          { label: 'Chế độ vận hành', value: 'Đóng hàng (Pack) • Kiểm hàng (Check) • Hoàn hàng (Return)' }
        ]
      },
      {
        groupTitle: 'Lưu Trữ & Chia Sẻ Dữ Liệu',
        rows: [
          { label: 'Vị trí lưu trữ gốc', value: 'Lưu trực tiếp trên ổ cứng máy tính khách hàng (Local Storage)' },
          { label: 'Chia sẻ trực tuyến', value: 'Tích hợp CDN miễn phí (sinh Link rút gọn xem ngay trên trình duyệt)' },
          { label: 'Định dạng xuất file', value: 'MP4 chuẩn nén H.264/H.265 dung lượng nhẹ, hình ảnh sắc nét' },
          { label: 'Tự động dọn dẹp ổ cứng', value: 'Hỗ trợ thiết lập tự động xóa video cũ theo số ngày tùy chỉnh' }
        ]
      },
      {
        groupTitle: 'Yêu Cầu Cấu Hình Máy Tính Khuyến Nghị',
        rows: [
          { label: 'Hệ điều hành', value: 'Windows 10 / Windows 11 (64-bit)' },
          { label: 'Vi xử lý (CPU)', value: 'Intel Core i3-14100 trở lên (cho 5–8 bàn đóng gói)' },
          { label: 'Bộ nhớ RAM', value: 'Tối thiểu 8GB RAM (Khuyến nghị 16GB RAM)' },
          { label: 'Ổ cứng lưu trữ', value: 'SSD 256GB cài Win + HDD 2TB–4TB lưu trữ Video' }
        ]
      }
    ],
    faqs: [
      {
        q: 'Một máy tính cài phần mềm ECAMPMCS có thể quản lý được tối đa bao nhiêu bàn đóng hàng?',
        a: 'Giá phần mềm tính theo đầu máy tính và KHÔNG GIỚI HẠN số bàn đóng gói trên mỗi máy tính: Gói 1 bàn đóng hàng giá 3.000.000đ/năm/máy tính (~8.000đ/ngày | $120/year/PC); gói ECAM Extend từ 2 bàn trở lên giá 5.000.000đ/năm/máy tính (~14.000đ/ngày | $200/year/PC); gói ECAM Ultimate tích hợp mọi camera sẵn có giá 8.500.000đ/năm/máy tính ($340/year/PC); Combo phần cứng Camera đóng hàng từ 4.500.000đ/bàn đóng gói. Số lượng camera kết nối thực tế chỉ phụ thuộc cấu hình phần cứng PC (ví dụ CPU Intel Core i3-14100, RAM 8GB chạy mượt 10–16 camera tương đương 5–8 bàn đóng gói).'
      },
      {
        q: 'Video quay xong được lưu ở đâu và làm sao để gửi cho Shopee, TikTok Shop khi khiếu nại?',
        a: 'Video gốc được lưu trực tiếp trên ổ cứng máy tính tại kho của bạn (không giới hạn thời hạn lưu). Khi cần gửi bằng chứng cho sàn TMĐT hoặc khách hàng, bạn chỉ cần nhấn nút "Chia sẻ" trên phần mềm để lấy đường link CDN trực tuyến (hoặc xuất file MP4) và gửi đi ngay lập tức.'
      },
      {
        q: 'Phần mềm có giới hạn số lượng video, thời lượng video hay thời hạn lưu trữ không?',
        a: 'Hoàn toàn không. Mỗi máy tính cài đặt ECAMPMCS đều KHÔNG GIỚI HẠN số bàn đóng gói, KHÔNG GIỚI HẠN số lượng video, KHÔNG GIỚI HẠN thời lượng video và KHÔNG GIỚI HẠN thời hạn lưu trữ, đồng thời miễn phí hoàn toàn tính năng chia sẻ link qua CDN.'
      },
      {
        q: 'Kho của tôi ở tỉnh xa thì PMCS TECH cài đặt và hỗ trợ kỹ thuật như thế nào?',
        a: 'Đội ngũ kỹ sư của PMCS TECH hỗ trợ cài đặt, cấu hình camera và hướng dẫn nhân viên kho sử dụng trực tiếp từ xa qua UltraViewer / AnyDesk và Zalo nhóm 1-1 cực kỳ nhanh chóng trên toàn quốc.'
      }
    ]
  },

  // ==========================================================================
  // 2. MÁY KIỂM KHO OPTICON PX-36 (DANH MỤC: THIẾT BỊ MÃ VẠCH / PDA KIỂM KHO)
  // ==========================================================================
  '/product/may-kiem-kho-opticon-px-36/': {
    path: '/product/may-kiem-kho-opticon-px-36/',
    categoryLabel: 'Máy Kiểm Kho PDA Mã Vạch',
    sku: 'OPTICON-PX36',
    brand: 'OPTICON (Nhật Bản)',
    origin: 'Thương hiệu Nhật Bản — Nhập khẩu chính ngạch CO/CQ',
    warranty: 'Bảo hành chính hãng 12 – 24 tháng theo tiêu chuẩn nhà sản xuất',
    statusText: 'Còn hàng chính hãng • Hỗ trợ mượn thiết bị Demo dự án',
    priceNote: 'Giá dự án B2B ưu đãi • Đầy đủ chứng nhận xuất xứ (CO), chất lượng (CQ) và hóa đơn VAT',
    shortIntro:
      'Thiết bị kiểm kho cầm tay PDA Android 12 hiệu năng cao từ thương hiệu Opticon Nhật Bản, tích hợp đầu đọc Imager 2D tốc độ 60 fps và chuẩn kháng bụi nước IP65.',
    shortBullets: [
      'Thương hiệu: Opticon Nhật Bản — Thiết kế công thái học chỉ 251g, cầm nắm thoải mái suốt ca làm việc.',
      'Hệ điều hành Android 12, vi xử lý 8 nhân MediaTek MT8768 (2.0GHz), RAM 4GB / ROM 64GB xử lý dữ liệu tức thì.',
      'Màn hình cảm ứng 5 inch Full HD (1920 x 1080), kính cường lực Gorilla Glass 3 chống trầy xước.',
      'Đầu quét CMOS Imager 2D (60 fps) đọc siêu nhạy mọi mã vạch 1D, 2D, QR Code, PDF417 ngay cả khi bị mờ, rách hoặc in trên bề mặt cong.',
      'Pin tháo rời dung lượng lớn 4000mAh hỗ trợ Hot-swap và sạc nhanh (đầy pin chỉ trong ~2.5 giờ).',
      'Độ bền chuẩn công nghiệp: Chịu rơi từ độ cao 1.5m xuống nền bê tông và kháng bụi/nước chuẩn IP65.'
    ],
    promoBox: {
      badgeTitle: 'Mua Máy Đọc Mã Vạch & Máy Kiểm Kho Tặng Ngay',
      subtitle: 'Gói hỗ trợ kỹ thuật & tích hợp hệ thống chuyên sâu từ PMCS TECH',
      theme: 'barcode',
      items: [
        'Tư vấn giải pháp quản lý kho bằng mã vạch hoàn toàn miễn phí.',
        'Cung cấp bộ SDK chuẩn (Android Studio, Java) hỗ trợ lập trình tích hợp vào ERP / WMS.',
        'Hỗ trợ cài đặt đồng bộ với phần mềm quay video đóng hàng ECAMPMCS.',
        'Chính sách cho mượn thiết bị test thực tế tại kho doanh nghiệp.'
      ]
    },
    articleBlocks: [
      {
        type: 'heading',
        id: 'tong-quan-opticon-px36',
        text: 'Máy Kiểm Kho OPTICON PX-36 – Thiết Bị Kiểm Kho Hiệu Năng Cao, Bền Bỉ Và Linh Hoạt'
      },
      {
        type: 'paragraph',
        text: 'Trong kỷ nguyên số hóa, nơi dữ liệu và tốc độ xử lý quyết định hiệu quả vận hành, các doanh nghiệp ngày càng cần đến những thiết bị di động mạnh mẽ, bền bỉ nhưng vẫn nhỏ gọn và dễ sử dụng. Đáp ứng trọn vẹn những yêu cầu đó, Opticon – thương hiệu hàng đầu thế giới về giải pháp thu thập dữ liệu đến từ Nhật Bản – đã cho ra đời Máy Kiểm Kho OPTICON PX-36: một thiết bị kiểm kho cầm tay mạnh mẽ, tích hợp máy quét mã vạch 2D tiên tiến, vận hành trên nền tảng Android 12 hiện đại.'
      },
      {
        type: 'subheading',
        text: '1. Hiệu Năng Vượt Trội – Đáp Ứng Mọi Nhu Cầu Công Việc'
      },
      {
        type: 'paragraph',
        text: 'Máy kiểm kho OPTICON PX-36 được trang bị bộ vi xử lý tám nhân (Octa-core) MediaTek MT8768 tốc độ 2.0GHz, kết hợp cùng bộ nhớ 4GB RAM và 64GB ROM (hỗ trợ mở rộng qua thẻ nhớ MicroSD lên đến 128GB). Cấu hình này giúp thiết bị xử lý mượt mà các ứng dụng quản lý kho WMS, ERP và tra cứu tồn kho theo thời gian thực.'
      },
      {
        type: 'paragraph',
        text: 'Màn hình cảm ứng điện dung 5 inch độ phân giải Full HD (1920 x 1080 pixels), sử dụng kính cường lực Gorilla Glass 3 và độ sáng 380 cd/m² giúp hiển thị sắc nét ngay cả dưới ánh đèn cao áp nhà xưởng hoặc ngoài trời.'
      },
      {
        type: 'image',
        imageSrc: '/wp-content/uploads/2025/06/may-kiem-kho-opticon-px36-5.jpg',
        imageAlt: 'Máy kiểm kho Opticon PX-36 chính hãng phân phối bởi PMCS TECH',
        imageCaption: 'Thiết kế gọn nhẹ 251g nhưng đạt chuẩn độ bền công nghiệp IP65 và chống rơi 1.5m của Opticon PX-36.'
      },
      {
        type: 'subheading',
        text: '2. Khả Năng Quét Mã Vạch 1D/2D Tốc Độ Cao 60 FPS'
      },
      {
        type: 'paragraph',
        text: 'Điểm mạnh cốt lõi của dòng máy Opticon chính là công nghệ quang học Nhật Bản. Máy kiểm kho OPTICON PX-36 sử dụng cảm biến CMOS (1280 x 800 pixels) với tốc độ quét lên tới 60 khung hình/giây, tích hợp đèn ngắm LED đỏ và đèn chiếu sáng trắng, cho phép giải mã tức thì mọi chuẩn mã vạch 1D (Code 128, EAN-13, Code 39, GS1 DataBar...) và 2D (QR Code, DataMatrix, PDF417, Aztec...) kể cả khi tem nhãn bị trầy xước, nhăn hoặc thiếu sáng.'
      },
      {
        type: 'subheading',
        text: '3. Kết Nối Toàn Diện & Pin Bền Bỉ Hỗ Trợ Hot-Swap'
      },
      {
        type: 'list',
        items: [
          'Kết nối không dây đa băng tần: Hỗ trợ 4G LTE (Nano SIM), Wi-Fi băng tần kép 802.11 a/b/g/n/ac, Bluetooth 5.0 BLE, NFC (13.56MHz) và định vị GPS/GLONASS/Galileo.',
          'Pin Lithium-Ion 3.85V dung lượng 4000mAh có thể tháo rời dễ dàng, tích hợp pin backup hỗ trợ thay pin nóng (Hot-swap) không làm gián đoạn phiên làm việc.',
          'Trang bị bộ đôi Camera 13MP (sau, tự động lấy nét + đèn Flash LED) và 5MP (trước) giúp chụp ảnh biên bản giao nhận hoặc tình trạng kiện hàng ngay tại hiện trường.'
        ]
      },
      {
        type: 'subheading',
        text: '4. Ứng Dụng Thực Tế Đa Ngành'
      },
      {
        type: 'list',
        items: [
          'Logistics & Kho vận: Kiểm kê hàng hóa xuất nhập kho, đối soát mã vận đơn, định vị vị trí kệ hàng.',
          'Bán lẻ & Siêu thị: Kiểm tra giá bán, tra cứu tồn kho tại quầy, quản lý chương trình khuyến mãi.',
          'Y tế & Dược phẩm: Quản lý hồ sơ bệnh án, theo dõi lô thuốc, kiểm soát mẫu xét nghiệm chính xác.',
          'Sản xuất công nghiệp: Truy xuất nguồn gốc bán thành phẩm trên dây chuyền lắp ráp.'
        ]
      }
    ],
    specGroups: [
      {
        groupTitle: 'Thông Tin Sản Phẩm',
        rows: [
          { label: 'Thương hiệu', value: 'OPTICON (Nhật Bản)' },
          { label: 'Model', value: 'PX-36' },
          { label: 'Loại thiết bị', value: 'Máy kiểm kho PDA tích hợp đầu đọc mã vạch 2D' },
          { label: 'Trọng lượng & Kích thước', value: '251 g • 155 x 71 x 16.8 mm' }
        ]
      },
      {
        groupTitle: 'Màn Hình & Hiển Thị',
        rows: [
          { label: 'Kích thước màn hình', value: '5.0 inch IPS Full HD (1920 x 1080 pixels)' },
          { label: 'Mặt kính bảo vệ', value: 'Kính cường lực Corning Gorilla Glass 3' },
          { label: 'Độ sáng màn hình', value: '380 cd/m² (hiển thị rõ ngoài trời & nhà xưởng)' }
        ]
      },
      {
        groupTitle: 'Hiệu Năng & Hệ Điều Hành',
        rows: [
          { label: 'Hệ điều hành', value: 'Android 12' },
          { label: 'Bộ vi xử lý (CPU)', value: 'MediaTek MT8768 Octa-core (8 nhân) 2.0 GHz' },
          { label: 'Bộ nhớ RAM / ROM', value: '4GB RAM / 64GB Flash ROM' },
          { label: 'Khe cắm thẻ nhớ', value: 'MicroSD mở rộng tối đa 128GB' }
        ]
      },
      {
        groupTitle: 'Khả Năng Quét Mã Vạch & Camera',
        rows: [
          { label: 'Công nghệ quét', value: '2D CMOS Imager (1280 x 800 pixels), tốc độ 60 fps' },
          { label: 'Mã vạch 1D hỗ trợ', value: 'Code 39, Code 128, EAN/UPC, GS1 DataBar, Codabar, MSI...' },
          { label: 'Mã vạch 2D hỗ trợ', value: 'QR Code, MicroQR, DataMatrix, PDF417, Aztec, MaxiCode...' },
          { label: 'Camera tích hợp', value: 'Camera sau 13.0 MP (Auto-focus + LED Flash) • Camera trước 5.0 MP' }
        ]
      },
      {
        groupTitle: 'Kết Nối, Pin & Độ Bền Công Nghiệp',
        rows: [
          { label: 'Mạng di động & Wi-Fi', value: '4G LTE (Nano SIM) • Wi-Fi 802.11 a/b/g/n/ac (Dual-band)' },
          { label: 'Bluetooth / NFC / GPS', value: 'Bluetooth 5.0 • NFC ISO14443A/B, Mifare • GPS/GLONASS/Galileo' },
          { label: 'Dung lượng Pin', value: 'Li-Ion 3.85V 4000mAh tháo rời + Hỗ trợ Hot-swap • Sạc đầy ~2.5 giờ' },
          { label: 'Chuẩn bảo vệ & Chống rơi', value: 'Chuẩn kháng bụi nước IP65 • Chịu rơi 1.5m xuống bê tông' }
        ]
      }
    ],
    faqs: [
      {
        q: 'Máy kiểm kho Opticon PX-36 có cài được phần mềm quản lý kho riêng của doanh nghiệp không?',
        a: 'Hoàn toàn được. Opticon PX-36 chạy hệ điều hành Android 12 mở và đi kèm bộ công cụ phát triển phần mềm (SDK) đầy đủ từ Opticon, cho phép cài đặt dễ dàng mọi ứng dụng APK nội bộ, ERP, SAP, Odoo hoặc phần mềm kiểm kho tùy chỉnh.'
      },
      {
        q: 'Tính năng thay pin nóng (Hot-swap) trên Opticon PX-36 hoạt động như thế nào?',
        a: 'Nhờ viên pin phụ (backup battery) tích hợp bên trong thân máy, nhân viên kho có thể tháo viên pin chính 4000mAh đã cạn và lắp viên pin mới vào mà máy không bị tắt nguồn hay mất dữ liệu đang kiểm kê dở dang.'
      },
      {
        q: 'PMCS TECH có hỗ trợ giấy tờ CO, CQ cho dự án đấu thầu không?',
        a: 'Có. Mọi thiết bị Opticon do PMCS TECH phân phối đều là hàng nhập khẩu chính ngạch, cung cấp đầy đủ chứng nhận xuất xứ (CO), chứng nhận chất lượng (CQ) và hóa đơn GTGT (VAT).'
      }
    ]
  },

  // ==========================================================================
  // 3. PIN SẠC DỰ PHÒNG BELKIN 10,000MAH MÀU HỒNG (DANH MỤC: SẠC DỰ PHÒNG)
  // ==========================================================================
  '/product/pin-sac-du-phong-belkin-10000mah-mau-hong/': {
    path: '/product/pin-sac-du-phong-belkin-10000mah-mau-hong/',
    categoryLabel: 'Sạc Dự Phòng Thương Hiệu Quốc Tế',
    sku: 'BELKIN-BPB006-PK',
    brand: 'BELKIN (Mỹ — Chính Hãng)',
    origin: 'Thương hiệu Belkin Hoa Kỳ • Nhập khẩu chính hãng',
    warranty: 'Bảo hành chính hãng 24 tháng (1 đổi 1 theo tiêu chuẩn Belkin)',
    statusText: 'Còn hàng chính hãng • Hỗ trợ in/khắc Logo doanh nghiệp',
    priceNote: 'Quà tặng công nghệ cao cấp thương hiệu Mỹ • Miễn phí thiết kế Demo Logo doanh nghiệp',
    shortIntro:
      'Pin sạc dự phòng Belkin Polymer Pocket Power 10,000mAh chính hãng thương hiệu Mỹ, sở hữu 3 cổng sạc tiện lợi (2 USB-A + 1 USB-C 15W) và hệ thống cảm biến an toàn chuẩn hàng không quốc tế.',
    shortBullets: [
      'Thương hiệu phụ kiện toàn cầu Belkin (Mỹ) với hơn 40 năm uy tín, được Apple khuyến nghị trong hệ sinh thái phụ kiện.',
      'Dung lượng thực 10,000mAh (lõi pin Lithium Polymer mật độ cao) — cung cấp thêm tới 36 giờ sử dụng cho điện thoại thông minh.',
      'Trang bị 3 cổng kết nối: 02 cổng USB-A và 01 cổng USB-C hai chiều (Input/Output công suất tối đa 15W), cho phép sạc đồng thời 2–3 thiết bị.',
      'Tích hợp cảm biến kiểm soát nhiệt độ, chống quá dòng, quá áp và ngắn mạch tuyệt đối an toàn khi mang lên máy bay.',
      'Vỏ nhựa Polycarbonate cao cấp chống bám vân tay, gam màu Hồng Rose Gold thanh lịch — rất được ưa chuộng làm quà tặng sự kiện 8/3, 20/10 và quà tặng VIP.'
    ],
    promoBox: {
      badgeTitle: 'Hỗ Trợ In Logo Theo Yêu Cầu – Hoàn Toàn Miễn Phí',
      subtitle: 'Giải pháp quà tặng công nghệ chính hãng cho doanh nghiệp & sự kiện',
      theme: 'gift',
      items: [
        'Miễn phí thiết kế mẫu Demo Logo trên thân pin Belkin trước khi sản xuất hàng loạt.',
        'Đặt hàng in ấn thương hiệu riêng với số lượng linh hoạt (MOQ ưu đãi cho doanh nghiệp).',
        'Đóng gói hộp quà sang trọng kèm cáp sạc USB-A to USB-C chính hãng trong hộp.',
        'Cam kết 100% hàng chính hãng Belkin, bảo hành 2 năm, xuất hóa đơn VAT đầy đủ.'
      ]
    },
    articleBlocks: [
      {
        type: 'heading',
        id: 'tong-quan-belkin-10000mah',
        text: 'Pin Sạc Dự Phòng Belkin Polymer Pocket Power 10,000mAh – An Toàn Chuẩn Quốc Tế, Quà Tặng Đẳng Cấp'
      },
      {
        type: 'paragraph',
        text: 'Khi lựa chọn pin sạc dự phòng cho nhu cầu cá nhân hoặc làm quà tặng đối tác, khách hàng VIP, yếu tố an toàn cháy nổ và uy tín thương hiệu luôn được đặt lên hàng đầu. Pin sạc dự phòng Belkin Polymer Pocket Power 10,000mAh (Màu Hồng Rose Gold) là sự kết hợp hoàn hảo giữa thiết kế thời trang, lõi pin Polymer bền bỉ và tiêu chuẩn kiểm định an toàn khắt khe của thương hiệu Belkin (Mỹ).'
      },
      {
        type: 'image',
        imageSrc: '/wp-content/uploads/2025/05/pin-du-phong-belkin-10000mah-13.jpg',
        imageAlt: 'Pin sạc dự phòng Belkin Polymer Pocket Power 10,000mAh màu hồng chính hãng',
        imageCaption: 'Thiết kế mỏng gọn chỉ 222g cùng tông màu Hồng Rose Gold tinh tế của Belkin Pocket Power 10,000mAh.'
      },
      {
        type: 'subheading',
        text: '1. Dung Lượng 10,000mAh Thực Tế – Sạc Cùng Lúc Nhiều Thiết Bị'
      },
      {
        type: 'paragraph',
        text: 'Với dung lượng 10,000mAh và hiệu suất chuyển đổi năng lượng cao, viên pin có thể sạc đầy từ 2 đến 2.5 lần cho các dòng iPhone, Samsung Galaxy, Xiaomi hoặc cung cấp năng lượng cho tai nghe Bluetooth, máy tính bảng trong suốt chuyến công tác dài ngày.'
      },
      {
        type: 'subheading',
        text: '2. Linh Hoạt Với 3 Cổng Giao Tiếp (2 USB-A & 1 USB-C 15W)'
      },
      {
        type: 'list',
        items: [
          '01 cổng USB-C hỗ trợ cả sạc vào (Input) và sạc ra (Output) với công suất tối đa 15W (5V/3A).',
          '02 cổng USB-A Output (5V/2.4A) tương thích rộng rãi với mọi loại cáp sạc hiện có trên thị trường.',
          'Hệ thống 4 đèn LED thông minh bên hông giúp theo dõi chính xác mức dung lượng pin còn lại (25% - 50% - 75% - 100%).'
        ]
      },
      {
        type: 'subheading',
        text: '3. Lựa Chọn Hàng Đầu Cho Quà Tặng Doanh Nghiệp Cao Cấp'
      },
      {
        type: 'paragraph',
        text: 'Tại PMCS TECH, dòng pin sạc dự phòng Belkin chính hãng được nhiều tập đoàn, ngân hàng và doanh nghiệp lựa chọn để in UV hoặc khắc laser logo thương hiệu làm quà tặng tri ân khách hàng, quà tặng hội nghị và sự kiện nội bộ nhờ chế độ bảo hành chính hãng lên tới 24 tháng.'
      }
    ],
    specGroups: [
      {
        groupTitle: 'Thông Tin Sản Phẩm',
        rows: [
          { label: 'Thương hiệu', value: 'Belkin (Hoa Kỳ)' },
          { label: 'Tên sản phẩm', value: 'Belkin Polymer Pocket Power 10,000mAh – Màu Hồng' },
          { label: 'Lõi pin', value: 'Lithium-Polymer (Li-Po) thế hệ mới an toàn cao' },
          { label: 'Dung lượng pin', value: '10,000 mAh / 37Wh (Đạt chuẩn mang lên máy bay)' }
        ]
      },
      {
        groupTitle: 'Cổng Kết Nối & Công Suất Sạc',
        rows: [
          { label: 'Tổng công suất đầu ra', value: '15W Max (5V / 3A)' },
          { label: 'Cổng sạc vào (Input)', value: '01 x USB-C (5V / 3A - 15W)' },
          { label: 'Cổng sạc ra (Output)', value: '02 x USB-A + 01 x USB-C (Sạc đồng thời nhiều thiết bị)' },
          { label: 'Phụ kiện đi kèm trong hộp', value: 'Cáp sạc USB-A to USB-C dài 15cm chính hãng Belkin' }
        ]
      },
      {
        groupTitle: 'Thiết Kế & Bảo Hành',
        rows: [
          { label: 'Kích thước & Trọng lượng', value: '131.8 x 68.3 x 15.7 mm • 222 g' },
          { label: 'Chất liệu vỏ', value: 'Nhựa Polycarbonate cao cấp chống cháy & hạn chế trầy xước' },
          { label: 'Tùy chỉnh quà tặng B2B', value: 'Hỗ trợ in UV / in lụa Logo doanh nghiệp sắc nét' },
          { label: 'Chế độ bảo hành', value: 'Bảo hành chính hãng 24 tháng' }
        ]
      }
    ],
    faqs: [
      {
        q: 'Pin sạc dự phòng Belkin 10,000mAh có được phép mang lên máy bay không?',
        a: 'Hoàn toàn được phép. Với dung lượng 10,000mAh (37Wh), sản phẩm thấp hơn nhiều so với ngưỡng giới hạn 100Wh của Cục Hàng không Quốc tế (IATA) và đạt đầy đủ chứng nhận an toàn chống cháy nổ.'
      },
      {
        q: 'Doanh nghiệp đặt mua số lượng lớn làm quà tặng có được in logo lên pin Belkin không?',
        a: 'Có. PMCS TECH hỗ trợ thiết kế demo miễn phí và in UV logo doanh nghiệp sắc nét trực tiếp lên bề mặt pin Belkin mà vẫn giữ nguyên tiêu chuẩn bảo hành chính hãng.'
      }
    ]
  },

  // ==========================================================================
  // 4. LOA BLUETOOTH KIÊM SẠC KHÔNG DÂY SP002 (DANH MỤC: LOA & SẠC KHÔNG DÂY LED)
  // ==========================================================================
  '/product/loa-bluetooth-kiem-sac-khong-day-sp002/': {
    path: '/product/loa-bluetooth-kiem-sac-khong-day-sp002/',
    categoryLabel: 'Loa Bluetooth & Sạc Không Dây Khắc LED Logo',
    sku: 'PMCS-SP002-LED',
    brand: 'PMCS TECH Gift & OEM',
    origin: 'Sản xuất OEM theo tiêu chuẩn kiểm định chất lượng PMCS TECH',
    warranty: 'Bảo hành 12 tháng đổi mới lỗi kỹ thuật',
    statusText: 'Nhận sản xuất & Khắc Laser LED Logo theo yêu cầu',
    priceNote: 'Giá B2B tận xưởng theo số lượng • Miễn phí thiết kế Demo Logo phát sáng LED',
    shortIntro:
      'Thiết bị công nghệ để bàn 3-trong-1 độc đáo: Loa Bluetooth 5.0 âm thanh sống động + Đế sạc nhanh không dây chuẩn Qi 15W + Giá đỡ điện thoại tích hợp đèn LED phát sáng Logo doanh nghiệp.',
    shortBullets: [
      'Thiết kế đa năng 3-trong-1: Loa Bluetooth nghe nhạc + Đế sạc không dây 15W + Giá đỡ điện thoại tiện lợi trên bàn làm việc.',
      'Tích hợp mặt đèn LED phát sáng Logo thương hiệu: Khắc laser logo doanh nghiệp lên bề mặt, phát sáng rực rỡ mỗi khi sử dụng hoặc cắm sạc.',
      'Chuẩn sạc nhanh không dây Qi công suất lên đến 15W (tự động điều chỉnh 5W / 7.5W / 10W / 15W tương thích iPhone & Android).',
      'Kết nối Bluetooth 5.0 ổn định trong phạm vi 10m, công suất loa 5W tích hợp màng cộng hưởng trầm (Passive Radiator) cho âm thanh ấm và rõ nét.',
      'Tích hợp mạch bảo vệ thông minh FOD (nhận diện vật thể lạ bằng kim loại), chống quá nhiệt và tự ngắt khi đầy pin.'
    ],
    promoBox: {
      badgeTitle: 'Hỗ Trợ Khắc Laser LED Logo – Hoàn Toàn Miễn Phí',
      subtitle: 'Quà tặng công nghệ để bàn nổi bật thương hiệu doanh nghiệp 24/7',
      theme: 'gift',
      items: [
        'Miễn phí 100% chi phí thiết kế & lên mẫu Demo Logo phát sáng LED.',
        'Nhận đặt hàng sản xuất B2B (MOQ linh hoạt từ 50 – 100 sản phẩm).',
        'Tùy chọn hộp quà tặng cứng cáp ép kim hoặc in logo đồng bộ theo bộ nhận diện.',
        'Xuất hóa đơn GTGT (VAT) đầy đủ & giao hàng miễn phí toàn quốc.'
      ]
    },
    articleBlocks: [
      {
        type: 'heading',
        id: 'tong-quan-sp002',
        text: 'SP002 – Loa Bluetooth Kiêm Sạc Không Dây Tích Hợp Đèn LED Logo Độc Đáo'
      },
      {
        type: 'paragraph',
        text: 'Làm thế nào để món quà tặng doanh nghiệp của bạn không bị cất vào ngăn kéo mà luôn hiện diện ngay trước mắt khách hàng và đối tác mỗi ngày? Mẫu Loa Bluetooth kiêm Sạc không dây SP002 tích hợp đèn LED Logo phát sáng chính là câu trả lời ấn tượng nhất cho xu hướng quà tặng công nghệ hiện đại.'
      },
      {
        type: 'image',
        imageSrc: '/wp-content/uploads/2025/04/san-xuat-oem-loa-bluetooth-tich-hop-sac-khong-day-1024x574.jpg',
        imageAlt: 'Sản xuất OEM Loa Bluetooth tích hợp sạc không dây khắc logo đèn LED SP002',
        imageCaption: 'Dây chuyền kiểm định chất lượng và khắc laser Logo phát sáng LED trên mẫu loa kiêm sạc không dây SP002.'
      },
      {
        type: 'subheading',
        text: '1. Hiệu Ứng Đèn LED Phát Sáng Logo Thương Hiệu Ấn Tượng'
      },
      {
        type: 'paragraph',
        text: 'Khác với phương pháp in mực thông thường dễ phai màu theo thời gian, SP002 sử dụng công nghệ khắc laser xuyên sáng trên bề mặt đế sạc. Khi người dùng bật loa hoặc đặt điện thoại lên sạc, hệ thống đèn LED bên dưới sẽ làm bừng sáng Logo và thông điệp của doanh nghiệp bạn một cách sang trọng và tinh tế.'
      },
      {
        type: 'subheading',
        text: '2. Trạm Giải Trí & Nạp Năng Lượng Ngay Trên Bàn Làm Việc'
      },
      {
        type: 'list',
        items: [
          'Sạc không dây chuẩn Qi 15W: Chỉ cần đặt điện thoại lên giá đỡ (theo chiều dọc hoặc chiều ngang) là thiết bị tự động nạp pin nhanh chóng mà không cần cắm rút dây rườm rà.',
          'Loa Bluetooth 5.0 công suất 5W: Mang lại âm thanh trong trẻo để nghe nhạc thư giãn, nghe podcast hoặc đàm thoại rảnh tay khi họp trực tuyến.',
          'Vừa sạc vừa xem video / họp Zoom: Thiết kế góc nghiêng công thái học giúp người dùng thoải mái theo dõi màn hình điện thoại trong lúc đang sạc không dây.'
        ]
      },
      {
        type: 'subheading',
        text: '3. An Toàn Tuyệt Đối Với Chip Quản Lý Nguồn Thông Minh'
      },
      {
        type: 'paragraph',
        text: 'Mỗi sản phẩm SP002 do PMCS TECH cung cấp đều được trang bị chip kiểm soát nhiệt độ, chống quá dòng, quá áp và cảm biến FOD tự động ngắt sạc nếu phát hiện chìa khóa, đồng xu hay vật thể kim loại lạ rơi vào vùng sạc.'
      }
    ],
    specGroups: [
      {
        groupTitle: 'Thông Tin Chung',
        rows: [
          { label: 'Mã sản phẩm', value: 'SP002' },
          { label: 'Dòng sản phẩm', value: 'Loa Bluetooth kiêm Đế sạc không dây khắc LED Logo' },
          { label: 'Chất liệu vỏ', value: 'Nhựa ABS chống cháy kết hợp phủ cao su non (Rubber Oil) mịn tay' },
          { label: 'Màu sắc tiêu chuẩn', value: 'Đen nhám sang trọng (Tôn nổi bật ánh sáng đèn LED Logo)' }
        ]
      },
      {
        groupTitle: 'Thông Số Loa Bluetooth & Âm Thanh',
        rows: [
          { label: 'Chuẩn kết nối không dây', value: 'Bluetooth V5.0 (Khoảng cách kết nối ≥ 10m)' },
          { label: 'Công suất loa', value: '5W (Tích hợp màng rung Bass trầm ấm)' },
          { label: 'Dung lượng pin tích hợp', value: '1,200mAh – 2,000mAh (Thời gian phát nhạc 4 – 6 giờ)' },
          { label: 'Tần số đáp ứng', value: '100Hz – 20KHz • Độ nhạy SNR ≥ 75dB' }
        ]
      },
      {
        groupTitle: 'Thông Số Sạc Không Dây & Cổng Nguồn',
        rows: [
          { label: 'Chuẩn sạc không dây', value: 'Chuẩn Qi quốc tế (Tương thích iPhone 8–16 Series, Samsung, Xiaomi...)' },
          { label: 'Công suất sạc không dây', value: 'Tự động điều chỉnh 5W / 7.5W / 10W / 15W Max' },
          { label: 'Cổng cấp nguồn (Input)', value: 'USB Type-C (5V/2A, 9V/2A QC3.0/PD)' },
          { label: 'Tính năng bảo vệ', value: 'Chống quá nhiệt, quá áp, ngắn mạch & Nhận diện vật lạ (FOD)' }
        ]
      },
      {
        groupTitle: 'Tùy Chỉnh Quà Tặng Doanh Nghiệp',
        rows: [
          { label: 'Công nghệ gia công Logo', value: 'Khắc Laser xuyên sáng tích hợp đèn LED phát sáng Logo' },
          { label: 'Số lượng đặt hàng tối thiểu', value: 'MOQ linh hoạt từ 50 – 100 pcs (Hỗ trợ làm mẫu duyệt trước)' },
          { label: 'Đóng gói tiêu chuẩn', value: 'Hộp quà tặng định hình + Cáp sạc Type-C + Hướng dẫn sử dụng' }
        ]
      }
    ],
    faqs: [
      {
        q: 'Đèn LED Logo trên loa SP002 phát sáng màu gì?',
        a: 'Mặc định đèn LED dưới vùng khắc laser phát ánh sáng Trắng tinh tế (hoặc tùy chỉnh màu LED Xanh dương / Đỏ / Vàng theo màu nhận diện thương hiệu khi đặt sản xuất OEM số lượng lớn).'
      },
      {
        q: 'Sạc không dây 15W có cần cắm dây nguồn cho loa không?',
        a: 'Pin tích hợp bên trong SP002 phục vụ cho việc phát nhạc Bluetooth di động và sáng đèn LED. Khi sử dụng tính năng sạc nhanh không dây 15W cho điện thoại, bạn chỉ cần cắm cáp Type-C cấp nguồn cho đế loa để đạt công suất sạc nhanh ổn định nhất.'
      }
    ]
  },

  // ==========================================================================
  // 5. SET QUÀ TẶNG CÔNG NGHỆ 5IN1 GS504 (DANH MỤC: SET QUÀ TẶNG DOANH NGHIỆP)
  // ==========================================================================
  '/product/set-qua-tang-cong-nghe-5in1-gs504/': {
    path: '/product/set-qua-tang-cong-nghe-5in1-gs504/',
    categoryLabel: 'Set Quà Tặng Công Nghệ Doanh Nghiệp VIP',
    sku: 'PMCS-GS504-5IN1',
    brand: 'PMCS TECH Corporate Gift',
    origin: 'Gia công & Đóng gói đồng bộ theo yêu cầu doanh nghiệp bởi PMCS TECH',
    warranty: 'Bảo hành 12 tháng toàn bộ phụ kiện điện tử trong Set quà',
    statusText: 'Nhận thiết kế & Gia công theo bộ nhận diện thương hiệu',
    priceNote: 'Báo giá chiết khấu B2B theo số lượng • Miễn phí thiết kế Layout 3D toàn bộ Set quà',
    shortIntro:
      'Bộ quà tặng công nghệ cao cấp 5-trong-1 GS504 đồng bộ nhận diện thương hiệu với hiệu ứng đèn LED phát sáng Logo trên tất cả các vật phẩm — giải pháp quà tặng VIP cho hội nghị, kỷ niệm thành lập và tri ân đối tác chiến lược.',
    shortBullets: [
      'Trọn bộ 5 vật phẩm công nghệ thiết thực nhất cho doanh nhân & dân văn phòng trong 1 hộp quà sang trọng.',
      '01 Sổ tay bìa da cao cấp tích hợp Sạc dự phòng 8,000mAh/10,000mAh, Sạc không dây và đèn LED phát sáng Logo ngoài bìa.',
      '01 Bình giữ nhiệt Inox 304 hiển thị nhiệt độ thông minh trên nắp cảm ứng LED, khắc laser Logo doanh nghiệp.',
      '01 Chuột không dây mỏng nhẹ tích hợp đèn LED phát sáng Logo thương hiệu trên lưng chuột.',
      '01 Bút ký kim loại cao cấp khắc tên/logo + 01 USB Flash Drive 16GB/32GB tích hợp ngay tại khóa cài sổ.',
      'Hộp quà cứng định hình lót mút EVA cắt CNC chuẩn xác từng chi tiết, kèm túi giấy sang trọng đồng bộ.'
    ],
    promoBox: {
      badgeTitle: 'Hỗ Trợ Thiết Kế & Khắc Logo Đồng Bộ 5 Món – Miễn Phí',
      subtitle: 'Giải pháp quà tặng đối tác VIP & sự kiện doanh nghiệp trọn gói',
      theme: 'gift',
      items: [
        'Miễn phí 100% thiết kế макет (Layout 3D) đồng bộ logo trên cả 5 món quà và vỏ hộp.',
        'Tùy biến linh hoạt thành phần trong Set quà theo ngân sách thực tế của doanh nghiệp.',
        'Kiểm định chất lượng từng linh kiện điện tử trước khi đóng hộp xuất xưởng.',
        'Hỗ trợ giao hàng đa điểm trên toàn quốc và xuất hóa đơn GTGT (VAT) đầy đủ.'
      ]
    },
    articleBlocks: [
      {
        type: 'heading',
        id: 'tong-quan-set-gs504',
        text: 'Set Quà Tặng Công Nghệ 5in1 GS504 – Đẳng Cấp Quà Tặng Doanh Nhân Thời Đại Số'
      },
      {
        type: 'paragraph',
        text: 'Thay vì những món quà truyền thống đơn lẻ, Set Quà Tặng Công Nghệ 5in1 GS504 mang đến trải nghiệm mở hộp đầy ấn tượng với 5 thiết bị công nghệ và phụ kiện văn phòng cao cấp được cá nhân hóa đồng bộ. Đặc biệt, công nghệ khắc laser kết hợp đèn LED phát sáng Logo trên bìa sổ đa năng và chuột không dây giúp thương hiệu của doanh nghiệp tỏa sáng đầy đẳng cấp.'
      },
      {
        type: 'image',
        imageSrc: '/wp-content/uploads/2025/04/qua-tang-doi-tac-hop-qua-tang-cong-nghe-cao-cap-4-trong-1-hop-qua-tet-2022.jpg',
        imageAlt: 'Set quà tặng công nghệ cao cấp GS504 khắc logo đèn LED đồng bộ cho đối tác doanh nghiệp',
        imageCaption: 'Hộp quà tặng công nghệ định hình cao cấp với logo phát sáng LED đồng bộ trên các thiết bị.'
      },
      {
        type: 'subheading',
        text: 'Chi Tiết 5 Vật Phẩm Trong Bộ Quà Tặng Công Nghệ GS504'
      },
      {
        type: 'list',
        items: [
          '1. Sổ Tay Đa Năng Tích Hợp Sạc Dự Phòng & Sạc Không Dây (LED Logo): Bìa da PU cao cấp tích hợp viên pin dự phòng 8,000mAh – 10,000mAh, bề mặt sạc không dây chuẩn Qi cho điện thoại, cáp sạc 3 đầu ẩn gọn ở bìa sau và mặt logo phát sáng đèn LED.',
          '2. USB Flash Drive 16GB/32GB Tích Hợp Khóa Cài: Nằm gọn ngay trên đai khóa nam châm của cuốn sổ tay, giúp lưu trữ tài liệu họp, hồ sơ năng lực (Profile) hoặc video giới thiệu doanh nghiệp.',
          '3. Chuột Không Dây LED Logo: Kết nối Wireless 2.4GHz / Bluetooth mượt mà, bấm êm không gây tiếng ồn (Silent Click), phần lưng chuột khắc laser phát sáng logo thương hiệu.',
          '4. Bình Giữ Nhiệt Inox 304 Hiển Thị Nhiệt Độ: Giữ nóng/lạnh từ 8–12 giờ, nắp bình tích hợp cảm biến chạm hiển thị nhiệt độ nước bằng đèn LED thông minh.',
          '5. Bút Ký Kim Loại Doanh Nhân: Thân kim loại sơn tĩnh điện sang trọng, khắc laser logo hoặc tên người nhận sắc nét.'
        ]
      },
      {
        type: 'subheading',
        text: 'Tại Sao Các Tập Đoàn & Ngân Hàng Ưu Tiên Chọn Set Quà GS504 Tại PMCS TECH?'
      },
      {
        type: 'list',
        items: [
          'Tính ứng dụng thực tế 100%: Cả 5 món trong hộp quà đều phục vụ trực tiếp cho công việc hàng ngày của cấp quản lý, giám đốc và nhân sự văn phòng.',
          'Tùy biến linh hoạt theo ngân sách: Doanh nghiệp có thể tùy chọn phối ghép 3in1, 4in1 hoặc 5in1 (thay thế bằng loa Bluetooth, củ sạc nhanh, tai nghe...) phù hợp với từng nhóm khách hàng.',
          'Cam kết tiến độ & chất lượng B2B: Sản xuất đúng tiến độ sự kiện, bảo hành uy tín 12 tháng cho toàn bộ linh kiện công nghệ.'
        ]
      }
    ],
    specGroups: [
      {
        groupTitle: 'Thành Phần Bộ Quà Tặng 5in1 GS504',
        rows: [
          { label: 'Mã bộ quà tặng', value: 'GS504 (5-in-1 Tech Corporate Gift Set)' },
          { label: 'Vật phẩm 01', value: 'Sổ tay bìa da tích hợp Pin dự phòng 8,000mAh + Sạc không dây + Đèn LED Logo' },
          { label: 'Vật phẩm 02', value: 'USB 16GB / 32GB tích hợp tại khóa nam châm của sổ tay' },
          { label: 'Vật phẩm 03', value: 'Chuột không dây Silent Click tích hợp đèn LED phát sáng Logo' },
          { label: 'Vật phẩm 04', value: 'Bình giữ nhiệt Inox 304 (500ml) có màn hình LED báo nhiệt độ' },
          { label: 'Vật phẩm 05', value: 'Bút ký kim loại cao cấp khắc Laser Logo / Tên cá nhân hóa' }
        ]
      },
      {
        groupTitle: 'Thông Số Kỹ Thuật Sổ Đa Năng & Chuột',
        rows: [
          { label: 'Pin tích hợp trong sổ', value: 'Lithium-Polymer 8,000mAh / 10,000mAh (Kèm cáp 3 đầu Lightning, Type-C, Micro)' },
          { label: 'Sạc không dây trên bìa sổ', value: 'Chuẩn Qi tương thích iPhone & Android' },
          { label: 'Chuột không dây', value: 'Wireless 2.4GHz + Pin sạc lại được • Có đèn LED sáng Logo' },
          { label: 'Chất liệu bình giữ nhiệt', value: 'Ruột Inox 304 an toàn thực phẩm • Dung tích 500ml' }
        ]
      },
      {
        groupTitle: 'Quy Cách Đóng Gói & Gia Công Thương Hiệu',
        rows: [
          { label: 'Hộp đựng & Túi xách', value: 'Hộp cứng mỹ thuật định hình mút EVA đen + Túi giấy đồng bộ' },
          { label: 'Kỹ thuật in/khắc Logo', value: 'Khắc Laser LED phát sáng + Ép kim / In UV trên vỏ hộp' },
          { label: 'Số lượng đặt tối thiểu', value: 'MOQ từ 50 – 100 bộ (Miễn phí thiết kế 3D & làm mẫu)' },
          { label: 'Bảo hành', value: '12 tháng chính hãng tại PMCS TECH' }
        ]
      }
    ],
    faqs: [
      {
        q: 'Doanh nghiệp có thể thay đổi các món bên trong Set quà GS504 theo ngân sách riêng không?',
        a: 'Hoàn toàn được. PMCS TECH trực tiếp gia công khay mút EVA định hình theo yêu cầu, do đó quý doanh nghiệp có thể tùy chọn giữ nguyên 5 món hoặc thay đổi sang Pin dự phòng Belkin/Recci, Loa Bluetooth SP002, Đế sạc đa năng hay Tai nghe không dây.'
      },
      {
        q: 'Thời gian sản xuất và khắc logo cho đơn hàng 100 – 500 bộ quà tặng GS504 mất bao lâu?',
        a: 'Sau khi chốt thiết kế 3D và duyệt mẫu thực tế, thời gian hoàn thiện và kiểm định chất lượng dao động từ 7 đến 14 ngày làm việc tùy số lượng. Với các sự kiện gấp, PMCS TECH có phương án ưu tiên tiến độ để kịp ngày tổ chức hội nghị của quý khách.'
      }
    ]
  }
};

/**
 * Trả về dữ liệu bài viết Blog gốc từ Database WordPress (52 bài viết)
 */
export function getWpBlogPostContent(route: RouteEntry): WpDbPost | null {
  return postCache[route.path] || null;
}

/**
 * Trả về danh sách danh mục Sản phẩm (product_cat) và Bài viết (category) từ Database WordPress
 */
export function getWpCategories() {
  return {
    productCategories: wpCategories.productCategories,
    postCategories: wpCategories.postCategories
  };
}

/**
 * Trả về dữ liệu chi tiết đầy đủ cho sản phẩm (kết hợp 5 sản phẩm mẫu chuẩn 1:1
 * cùng toàn bộ 112 sản phẩm và 98 thuộc tính kỹ thuật từ Database WordPress gốc).
 */
export function getRichProductContent(route: RouteEntry): RichProductData {
  const dbProd = prodCache[route.path];
  const exact = RICH_PRODUCTS_BY_PATH[route.path];

  if (exact) {
    return {
      ...exact,
      sku: dbProd?.sku || route.sku || exact.sku,
      gallery: dbProd?.gallery && dbProd.gallery.length > 0 ? dbProd.gallery : undefined,
      specGroups:
        dbProd && dbProd.specGroups && dbProd.specGroups.length > 0 && !route.path.includes('ecampmcs')
          ? dbProd.specGroups
          : exact.specGroups
    };
  }

  const p = route.path.toLowerCase();
  const catList = dbProd?.categories || route.categories || [];
  const catNames = catList.map((c) => c.name).join(' • ') || '';

  const isSoftware = p.includes('ecampmcs') || p.includes('packing-video-software');
  const isBarcodeOrRfid =
    p.includes('opticon') ||
    p.includes('may-quet') ||
    p.includes('may-doc') ||
    p.includes('may-kiem-kho') ||
    p.includes('rfid') ||
    catList.some((c) => c.slug.includes('ma-vach') || c.slug.includes('kiem-kho') || c.slug.includes('rfid'));

  if (isSoftware) {
    return {
      ...RICH_PRODUCTS_BY_PATH['/product/phan-mem-quay-video-dong-hang-ecampmcs/'],
      path: route.path,
      sku: dbProd?.sku || route.sku || 'ECAMPMCS-PRO',
      rawContentHtml: dbProd?.contentHtml || undefined
    };
  }

  const defaultBullets = isBarcodeOrRfid
    ? [
        'Cảm biến quang học Opticon Nhật Bản giải mã siêu nhạy mã vạch 1D/2D, QR Code, CCCD và tem vận đơn TMĐT.',
        'Thiết kế công nghiệp bền bỉ, chống va đập rơi rớt và kháng bụi/nước đạt chuẩn quốc tế.',
        'Tương thích hoàn hảo 100% với phần mềm quay video đóng hàng ECAMPMCS và các hệ thống ERP, WMS, POS.',
        'Nhập khẩu chính ngạch đầy đủ CO, CQ và hóa đơn VAT — Bảo hành chính hãng 12–24 tháng.'
      ]
    : [
        'Gia công khắc Laser LED phát sáng hoặc in UV Logo thương hiệu sắc nét theo bộ nhận diện doanh nghiệp.',
        'Kiểu dáng hiện đại, chất liệu bền bỉ, đạt tiêu chuẩn an toàn kỹ thuật và kiểm định chất lượng nghiêm ngặt.',
        'Đóng gói hộp quà định hình sang trọng, nâng tầm giá trị thương hiệu khi trao tặng đối tác & khách hàng VIP.',
        'Miễn phí thiết kế mẫu Demo, chiết khấu B2B tận gốc và xuất hóa đơn GTGT (VAT) đầy đủ.'
      ];

  const resolvedShortIntro = dbProd?.shortIntro || route.shortIntro || undefined;
  const resolvedBullets =
    dbProd?.shortBullets && dbProd.shortBullets.length > 0
      ? dbProd.shortBullets
      : route.shortBullets && route.shortBullets.length > 0
      ? route.shortBullets
      : defaultBullets;

  return {
    path: route.path,
    categoryLabel: catNames || (isBarcodeOrRfid ? 'Thiết Bị Mã Vạch & Kiểm Kho' : 'Phụ Kiện & Quà Tặng Công Nghệ'),
    sku: dbProd?.sku || route.sku || route.path.replace(/^\/product\/|\/$/g, '').toUpperCase().slice(0, 18),
    brand: isBarcodeOrRfid
      ? 'OPTICON (Nhật Bản — Phân Phối Chính Hãng)'
      : p.includes('belkin')
      ? 'BELKIN (Mỹ — Chính Hãng)'
      : p.includes('recci')
      ? 'RECCI Chính Hãng'
      : 'PMCS TECH Gift & OEM',
    origin: isBarcodeOrRfid ? 'Thương hiệu Nhật Bản • Nhập khẩu chính ngạch CO/CQ' : 'Phân phối & Gia công Logo bởi PMCS TECH',
    warranty: isBarcodeOrRfid || p.includes('belkin') ? 'Bảo hành chính hãng 24 tháng' : 'Bảo hành chính hãng 12 tháng',
    statusText: isBarcodeOrRfid
      ? 'Còn hàng chính hãng • Giao miễn phí toàn quốc'
      : 'Còn hàng • Hỗ trợ In / Khắc Laser LED Logo theo yêu cầu',
    priceNote: isBarcodeOrRfid
      ? 'Nhập khẩu chính ngạch • Đầy đủ chứng nhận CO/CQ & hóa đơn GTGT (VAT)'
      : 'Chiết khấu B2B hấp dẫn theo số lượng • Miễn phí thiết kế Demo Logo doanh nghiệp',
    shortIntro: resolvedShortIntro,
    shortBullets: resolvedBullets,
    promoBox: isBarcodeOrRfid
      ? RICH_PRODUCTS_BY_PATH['/product/may-kiem-kho-opticon-px-36/'].promoBox
      : RICH_PRODUCTS_BY_PATH['/product/loa-bluetooth-kiem-sac-khong-day-sp002/'].promoBox,
    articleBlocks: [],
    rawContentHtml: dbProd?.contentHtml || undefined,
    gallery: dbProd?.gallery && dbProd.gallery.length > 0 ? dbProd.gallery : undefined,
    specGroups:
      dbProd?.specGroups && dbProd.specGroups.length > 0
        ? dbProd.specGroups
        : isBarcodeOrRfid
        ? RICH_PRODUCTS_BY_PATH['/product/may-kiem-kho-opticon-px-36/'].specGroups
        : RICH_PRODUCTS_BY_PATH['/product/loa-bluetooth-kiem-sac-khong-day-sp002/'].specGroups,
    faqs:
      dbProd?.faqs && dbProd.faqs.length > 0
        ? dbProd.faqs
        : isBarcodeOrRfid
        ? RICH_PRODUCTS_BY_PATH['/product/may-kiem-kho-opticon-px-36/'].faqs
        : RICH_PRODUCTS_BY_PATH['/product/loa-bluetooth-kiem-sac-khong-day-sp002/'].faqs
  };
}
